/**
 * @file EditCompanyDialog.tsx
 * @brief Sheet form for editing a company via `PUT /api/admin/companies/:id`.
 *
 * @details
 * - Pre-populates from the row; money fields (cents → major) on entry.
 * - Only changed fields are sent. Empty money → `null` (clear override).
 * - Status Active ↔ Suspended cascades to employees (backend).
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — FormSheet + GridBlock chrome
 */

import { FormSheetTabs, formSheetTabPanel, type FormSheetTab } from "@/components/ui/FormSheetTabs";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  BadgePoundSterling,
  Briefcase,
  Building2,
  LoaderCircle,
  MapPin,
} from "lucide-react";
import { AddressAutocomplete } from "@/components/ui/AddressAutocomplete";
import { Button } from "@/components/ui/Button";
import { CountryPicker } from "@/components/ui/CountryPicker";
import {
  FormFieldGroup,
  FormSheet,
  FormSheetAlert,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetField,
  FormSheetFields,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { Input } from "@/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { useSystemSettings } from "@/features/billing/api";
import { useUpdateCompany } from "../api";
import { ALL_COMPANY_RATE_FIELDS, CURRENCY_CODES } from "../companyRateFields";
import { type AdminCompany, type CompanyStatus, type UpdateCompanyInput } from "../schemas";
import { CompanyEnterpriseSection } from "./CompanyEnterpriseSection";
import { CompanyRateFieldsSection } from "./CompanyRateFieldsSection";

interface EditCompanyDialogProps {
  company: AdminCompany | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormValues {
  name: string;
  domain?: string;
  billing_email?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  phone_number?: string;
  tax_id?: string;
  status: CompanyStatus;

  [key: `custom_${string}_major`]: string | undefined;
}

function minorToMajorString(minor: number | string | null | undefined): string {
  if (minor == null || minor === "") return "";
  const n = typeof minor === "string" ? Number(minor) : minor;
  if (!Number.isFinite(n)) return "";
  return (n / 100).toFixed(2);
}

function majorToMinorOrNull(input?: string): number | null | undefined {
  if (input === undefined) return undefined;
  if (input.trim() === "") return null;
  const n = Number(input);
  if (!Number.isFinite(n)) return undefined;
  return Math.round(n * 100);
}

export function EditCompanyDialog({ company, open, onOpenChange }: EditCompanyDialogProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  // Tabs, as Edit user (DES 01-10-2026: "edit companies ko bhi same look do").
  const [tab, setTab] = useState<EditCompanyTab>("profile");
  // Footer slot for Enterprise's "Update plan", so it sits in the button row
  // at the same size (DES 01-10-2026: "should be in one row … equal size").
  const [planActionSlot, setPlanActionSlot] = useState<HTMLDivElement | null>(null);
  // And one at the row's left end, for the "Enterprise inquiries" link.
  const [footerLeftSlot, setFooterLeftSlot] = useState<HTMLDivElement | null>(null);
  const updateCompany = useUpdateCompany();
  const settingsQuery = useSystemSettings();

  const form = useForm<FormValues>({
    defaultValues: {
      name: "",
      status: "active",
    },
  });

  useEffect(() => {
    if (open && company) {
      setTab("profile");
      const productMajors: Record<string, string> = {};
      for (const f of ALL_COMPANY_RATE_FIELDS) {
        for (const c of CURRENCY_CODES) {
          productMajors[f.majorKey(c)] = minorToMajorString(
            company[f.minorKey(c) as keyof AdminCompany] as number | string | null | undefined,
          );
        }
      }
      form.reset({
        ...productMajors,
        name: company.name,
        domain: company.domain ?? "",
        billing_email: company.billing_email ?? "",
        address: company.address ?? "",
        city: company.city ?? "",
        state: company.state ?? "",
        postal_code: company.postal_code ?? "",
        country: company.country ?? "",
        phone_number: company.phone_number ?? "",
        tax_id: company.tax_id ?? "",
        status: company.status,
      });
      setServerError(null);
    }
  }, [open, company, form]);

  const watchedCountry = useWatch({ control: form.control, name: "country" });
  const rateOverrides = {
    miUsd: useWatch({
      control: form.control,
      name: "custom_boardroom_rate_usd_major",
    }),
    miEur: useWatch({
      control: form.control,
      name: "custom_boardroom_rate_eur_major",
    }),
    miGbp: useWatch({
      control: form.control,
      name: "custom_boardroom_rate_gbp_major",
    }),
    notesUsd: useWatch({
      control: form.control,
      name: "custom_note_rate_usd_major",
    }),
    notesEur: useWatch({
      control: form.control,
      name: "custom_note_rate_eur_major",
    }),
    notesGbp: useWatch({
      control: form.control,
      name: "custom_note_rate_gbp_major",
    }),
  };

  if (!company) return null;

  const onSubmit = form.handleSubmit(
    async (values) => {
      setServerError(null);

      const diff: UpdateCompanyInput = {};
      const setIfChanged = <K extends keyof UpdateCompanyInput>(
        key: K,
        next: UpdateCompanyInput[K],
        prev: UpdateCompanyInput[K] | string | number | null | undefined,
      ) => {
        if (next === undefined) return;
        if ((next ?? null) === (prev ?? null)) return;
        diff[key] = next;
      };

      setIfChanged("name", values.name.trim(), company.name);
      setIfChanged(
        "domain",
        values.domain?.trim() === "" ? "" : values.domain?.trim(),
        company.domain ?? "",
      );
      setIfChanged(
        "billing_email",
        values.billing_email?.trim() === "" ? "" : values.billing_email?.trim(),
        company.billing_email ?? "",
      );
      setIfChanged(
        "address",
        values.address?.trim() === "" ? "" : values.address?.trim(),
        company.address ?? "",
      );
      setIfChanged(
        "city",
        values.city?.trim() === "" ? "" : values.city?.trim(),
        company.city ?? "",
      );
      setIfChanged(
        "state",
        values.state?.trim() === "" ? "" : values.state?.trim(),
        company.state ?? "",
      );
      setIfChanged(
        "postal_code",
        values.postal_code?.trim() === "" ? "" : values.postal_code?.trim(),
        company.postal_code ?? "",
      );
      setIfChanged(
        "country",
        values.country?.trim() === "" ? "" : values.country?.trim(),
        company.country ?? "",
      );
      setIfChanged(
        "phone_number",
        values.phone_number?.trim() === "" ? "" : values.phone_number?.trim(),
        company.phone_number ?? "",
      );
      setIfChanged(
        "tax_id",
        values.tax_id?.trim() === "" ? "" : values.tax_id?.trim(),
        company.tax_id ?? "",
      );
      setIfChanged("status", values.status, company.status);

      for (const f of ALL_COMPANY_RATE_FIELDS) {
        for (const c of CURRENCY_CODES) {
          const minorKey = f.minorKey(c);
          const next = majorToMinorOrNull(values[f.majorKey(c)]);
          const prev =
            (company[minorKey as keyof AdminCompany] as number | null | undefined) ?? null;
          if (next !== undefined && next !== prev) {
            diff[minorKey] = next as number | undefined;
          }
        }
      }

      if (Object.keys(diff).length === 0) {
        toast({ title: "No changes to save" });
        onOpenChange(false);
        return;
      }

      try {
        await updateCompany.mutateAsync({ id: company.id, input: diff });
        toast({
          variant: "success",
          title: "Company updated",
          description: `${values.name} saved.`,
        });
        onOpenChange(false);
      } catch (err) {
        if (err instanceof ApiClientError) setServerError(err.message);
        else setServerError("Unexpected error. Please try again.");
      }
    },
    // The only required field (name) is on Profile: show it if it failed.
    () => setTab("profile"),
  );

  const willCascade =
    form.watch("status") !== company.status &&
    (form.watch("status") === "suspended" || form.watch("status") === "active");

  return (
    <FormSheet open={open} onOpenChange={onOpenChange}>
      {/* Opens like Cmd+K, as Edit user (DES 01-10-2026). */}
      <FormSheetContent side="palette">
        <form
          onSubmit={onSubmit}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          noValidate
        >
          <FormSheetFrame>
            <FormSheetHeader>
              {/* Status sits beside the title, not on a tab (DES 01-10-2026:
                  "status ko tab say nikal kar title kay right per rakho"). */}
              <div className="flex items-start justify-between gap-6">
                <div className="min-w-0 flex-1 space-y-1.5">
                  <FormSheetTitle>Edit company</FormSheetTitle>
                  <FormSheetDescription>
                    Profile, address, status and custom rates save with this form. Enterprise
                    activates on its own tab.
                  </FormSheetDescription>
                </div>
                {/* mr-10 clears the sheet's close ✕ in the corner. */}
                <div className="mr-10 flex shrink-0 flex-col items-end gap-1">
                  <Select
                    value={form.watch("status")}
                    onValueChange={(v) => form.setValue("status", v as CompanyStatus)}
                  >
                    <SelectTrigger
                      aria-label="Account status"
                      className="!h-9 !w-auto gap-2 !rounded-full !px-4 text-sm font-semibold focus:!ring-0 focus:!ring-offset-0 focus-visible:bg-foreground/5"
                    >
                      {/* A div: the trigger line-clamps its direct spans. */}
                      <div className="flex items-center gap-2">
                        <span
                          aria-hidden
                          className={cn(
                            "block h-2 w-2 shrink-0 rounded-full",
                            STATUS_DOT[form.watch("status")] ?? "bg-muted-foreground",
                          )}
                        />
                        <SelectValue />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="deleted">Deleted</SelectItem>
                    </SelectContent>
                  </Select>
                  {willCascade ? (
                    <p className="whitespace-nowrap text-right text-xs text-muted-foreground">
                      Employees will match
                    </p>
                  ) : null}
                </div>
              </div>
            </FormSheetHeader>

            <FormSheetTabs
              tabs={EDIT_COMPANY_TABS}
              value={tab}
              onChange={setTab}
              label="Edit company"
            />

            <FormSheetBody>
              {/* One section per tab; the sheet fits the open tab. */}
              <div className="grid">
                <div {...formSheetTabPanel(tab, "profile")}>
                  <FormSheetSection
                    hideHeading
                    title="Profile"
                    subtitle="Company identity and contact details."
                  >
                    <FormSheetFields>
                      <FormSheetField span={2}>
                        <FormFieldGroup label="Name" required>
                          <Input capitalizeFirst {...form.register("name", { required: true })} />
                        </FormFieldGroup>
                      </FormSheetField>
                      <FormSheetField>
                        <FormFieldGroup label="Domain">
                          <Input {...form.register("domain")} />
                        </FormFieldGroup>
                      </FormSheetField>
                      <FormSheetField>
                        <FormFieldGroup label="Billing email">
                          <Input type="email" {...form.register("billing_email")} />
                        </FormFieldGroup>
                      </FormSheetField>
                      <FormSheetField>
                        <FormFieldGroup label="Phone">
                          <Input {...form.register("phone_number")} />
                        </FormFieldGroup>
                      </FormSheetField>
                      <FormSheetField>
                        <FormFieldGroup label="Tax / VAT ID">
                          <Input autoCapitalize="characters" {...form.register("tax_id")} />
                        </FormFieldGroup>
                      </FormSheetField>
                    </FormSheetFields>
                  </FormSheetSection>
                </div>

                <div {...formSheetTabPanel(tab, "address")}>
                  <FormSheetSection
                    hideHeading
                    title="Address"
                    subtitle="Registered / billing address."
                  >
                    <FormSheetFields>
                      <FormSheetField span={2}>
                        <AddressAutocomplete
                          id="edit-company-address"
                          label="Street"
                          value={form.watch("address") || ""}
                          onChange={(street) =>
                            form.setValue("address", street, {
                              shouldDirty: true,
                            })
                          }
                          onSelect={(parts) => {
                            form.setValue("address", parts.address, {
                              shouldDirty: true,
                            });
                            if (parts.city) {
                              form.setValue("city", parts.city, {
                                shouldDirty: true,
                              });
                            }
                            if (parts.state) {
                              form.setValue("state", parts.state, {
                                shouldDirty: true,
                              });
                            }
                            if (parts.postal_code) {
                              form.setValue("postal_code", parts.postal_code, {
                                shouldDirty: true,
                              });
                            }
                            if (parts.country) {
                              form.setValue("country", parts.country, {
                                shouldDirty: true,
                              });
                            }
                          }}
                        />
                      </FormSheetField>
                    </FormSheetFields>
                    <FormSheetFields className="sm:grid-cols-3">
                      <FormSheetField>
                        <FormFieldGroup label="City">
                          <Input capitalizeFirst {...form.register("city")} />
                        </FormFieldGroup>
                      </FormSheetField>
                      <FormSheetField>
                        <FormFieldGroup label="State / region">
                          <Input capitalizeFirst {...form.register("state")} />
                        </FormFieldGroup>
                      </FormSheetField>
                      <FormSheetField>
                        <FormFieldGroup label="Postal code">
                          <Input autoCapitalize="characters" {...form.register("postal_code")} />
                        </FormFieldGroup>
                      </FormSheetField>
                      {/* Half width, beside Postal code (DES 01-10-2026: "keep it like others"). */}
                      <FormSheetField>
                        <FormFieldGroup label="Country">
                          <CountryPicker
                            value={form.watch("country") || null}
                            onChange={(name) => form.setValue("country", name ?? "")}
                            sheetSubtitle="Registered address country for this company."
                          />
                        </FormFieldGroup>
                      </FormSheetField>
                    </FormSheetFields>
                  </FormSheetSection>
                </div>

                <div {...formSheetTabPanel(tab, "rates")}>
                  <FormSheetSection
                    hideHeading
                    title="Custom rates (PAYG)"
                    subtitle="Per-product overrides. Empty = platform default. Saved with this form. Usage is gated by a Stripe card hold, not credit terms."
                  >
                    <FormSheetFields>
                      <FormSheetField span={2}>
                        <CompanyRateFieldsSection
                          register={form.register}
                          platformSettings={settingsQuery.data}
                          embedded
                        />
                      </FormSheetField>
                    </FormSheetFields>
                  </FormSheetSection>
                </div>

                <div {...formSheetTabPanel(tab, "enterprise")}>
                  <FormSheetSection
                    hideHeading
                    title="Enterprise plan"
                    subtitle="Optional sales-led seats and a shared Meeting Intel + Notes pool. Interpretation stays pay as you go. Saves on this tab, not with Save changes."
                  >
                    <FormSheetFields>
                      <FormSheetField span={2}>
                        <CompanyEnterpriseSection
                          companyId={company.id}
                          companyCountry={watchedCountry || company.country}
                          platformSettings={settingsQuery.data}
                          rateOverrides={rateOverrides}
                          footerSlot={tab === "enterprise" ? planActionSlot : null}
                          footerLeftSlot={tab === "enterprise" ? footerLeftSlot : null}
                        />
                      </FormSheetField>
                    </FormSheetFields>
                  </FormSheetSection>
                </div>
              </div>
              {serverError ? <FormSheetAlert>{serverError}</FormSheetAlert> : null}
            </FormSheetBody>

            <FormSheetFooter>
              <div ref={setFooterLeftSlot} className="mr-auto flex items-center" />
              <FormSheetFooterCell>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  disabled={updateCompany.isPending}
                  className={formSheetActionClass}
                >
                  Cancel
                </Button>
              </FormSheetFooterCell>
              <div ref={setPlanActionSlot} className="contents" />
              <FormSheetFooterCell>
                <Button
                  type="submit"
                  disabled={updateCompany.isPending}
                  className={cn(
                    formSheetActionClass,
                    "bg-primary text-primary-foreground hover:bg-primary/90",
                  )}
                >
                  {updateCompany.isPending ? (
                    <>
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    "Save changes"
                  )}
                </Button>
              </FormSheetFooterCell>
            </FormSheetFooter>
          </FormSheetFrame>
        </form>
      </FormSheetContent>
    </FormSheet>
  );
}

type EditCompanyTab = "profile" | "address" | "rates" | "enterprise";

const EDIT_COMPANY_TABS: FormSheetTab<EditCompanyTab>[] = [
  { id: "profile", label: "Profile", icon: Building2 },
  { id: "address", label: "Address", icon: MapPin },
  { id: "rates", label: "Rates", icon: BadgePoundSterling },
  { id: "enterprise", label: "Enterprise", icon: Briefcase },
];

/** Dot colour on the header's status pill. */
const STATUS_DOT: Record<string, string> = {
  active: "bg-success",
  suspended: "bg-warning",
  deleted: "bg-destructive",
};
